import React from "react";
import PropTypes from "prop-types";
import { Autocomplete, TextField, Chip } from "@mui/material";
import theme from "assets/theme";

/**
 * VuiAutocomplete
 * ----------------------------------------
 * Vision UI–native Autocomplete
 * - Aligned with VuiInputRoot
 * - Solid (non-gradient) by default
 * - Glass when gradient=true
 * - MUI styles are FORCE-OVERRIDDEN
 */
const VuiAutocomplete = ({
  multiple = false,
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  getOptionLabel = (opt) => opt?.name ?? "",
  isOptionEqualToValue = (opt, val) => opt?._id === val?._id,
  disabled = false,
  gradient = false,
  minWidth = 260,
  fullWidth = false,

  helperText,
  density = "comfortable",
  ...rest
}) => {
  const inputRootBase = {
    height: "auto !important",
    minHeight: "38px !important",
    display: "flex !important",
    alignItems: "center !important",
    // overflow: "visible !important",
    borderRadius: `${theme.borders.borderRadius.lg} !important`,
  };

  // const inputRootBase = {
  //   // minHeight: multiple ? "auto" : "38px !important",
  //   height: "38px !important",
  //   display: "flex !important",
  //   alignItems: multiple ? "flex-start !important" : "center !important",
  //   paddingTop: multiple ? "4px !important" : "0px !important",
  //   paddingBottom: multiple ? "4px !important" : "0px !important",
  // };

  const chipGradient = `linear-gradient(
    135deg,
    ${theme.palette.info.main},
    ${theme.palette.info.dark}
  )`;

  return (
    <Autocomplete
      disableClearable
      multiple={multiple}
      options={options}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      disableCloseOnSelect={multiple}
      disabled={disabled}
      size="small"
      slotProps={{
        popper: {
          sx: {
            zIndex: "1300 !important",
          },
        },

        /* ===============================
           DROPDOWN CONTAINER (MENU)
           ===============================
           - THIS controls the white box issue
           - Must override MUI Paper defaults
        */
        popper: {
          sx: {
            zIndex: "1300 !important",

            /* 🔥 THIS REMOVES THE WHITE CONTAINER */
            "& .MuiPaper-root": {
              backgroundColor: `${theme.palette.inputColors.backgroundColor} !important`,
              borderRadius: `${theme.borders.borderRadius.lg} !important`,
              border: `0.5px solid ${theme.palette.grey[600]} !important`,
              boxShadow: "0 14px 32px rgba(0,0,0,0.45) !important",
              padding: "0 !important",
              overflow: "hidden",
            },
          },
        },
      }}
      /* ===============================
         ROOT STYLING (MOST IMPORTANT)
         ===============================
      */
      sx={{
        width: fullWidth ? "100%" : "auto",
        minWidth: fullWidth ? "unset" : minWidth,

        /* === INPUT ROOT  === */
        "& .MuiAutocomplete-inputRoot": {
          ...inputRootBase,

          // backgroundColor: `${theme.palette.inputColors.backgroundColor} !important`,
          flexWrap: multiple ? "wrap !important" : "nowrap !important",
          border: `0.5px solid ${theme.palette.grey[600]} !important`,
          boxShadow: "none !important",
        },

        /* === GLASS MODE (ONLY WHEN gradient=true) === */
        ...(gradient && {
          "& .MuiAutocomplete-inputRoot": {
            ...inputRootBase,

            background:
              "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.01)) !important",
            backdropFilter: "blur(20px) !important",
            WebkitBackdropFilter: "blur(20px) !important",
            border: "1px solid rgba(255,255,255,0.2)",
            boxShadow: "none !important",
          },
        }),

        /* === KILL MUI OUTLINE (fieldset) === */
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },

        /* === INPUT TEXT (VERTICAL ALIGN FIX) === */
        "& .MuiAutocomplete-input": {
          paddingLeft: "12px !important",
          lineHeight: "38px !important",
          color: `${theme.palette.dark.main} !important`,
          fontSize: theme.typography.size.sm,

          minWidth:
            value && (Array.isArray(value) ? value.length > 0 : true)
              ? "0 !important"
              : "30px !important",

          width:
            value && (Array.isArray(value) ? value.length > 0 : true) ? "0 !important" : "auto",

          flexGrow:
            value && (Array.isArray(value) ? value.length > 0 : true)
              ? "0 !important"
              : "1 !important",
        },

        "& .MuiAutocomplete-input::placeholder": {
          color: `${theme.palette.dark.main} !important`,
          opacity: "0.6 !important",
          fontSize: "12px",
        },

        /* === CLEAR + DROPDOWN ICONS === */
        "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
          color: `${theme.palette.dark.main} !important`,
        },

        /* ===============================
           DROPDOWN LIST (NOT PAPER)
           ===============================
        */
        "& .MuiAutocomplete-listbox": {
          padding: "0px !important",
          margin: "0 !important",
          //   backgroundColor: "transparent !important",
          maxHeight: "120px",
        },

        /* === MENU OPTIONS === */
        "& .MuiAutocomplete-option": {
          minHeight: "14px !important",
          padding: "6px 12px !important",
          margin: "4px !important",

          display: "flex !important",
          alignItems: "center !important",

          borderRadius: "10px !important",
          fontSize: theme.typography.size.sm,

          color: `${theme.palette.grey[300]} !important`, // default text
          backgroundColor: "transparent !important",
        },

        /* === HOVER === */
        "& .MuiAutocomplete-option:hover": {
          backgroundColor: "rgba(255,255,255,0.10) !important",
          color: `${theme.palette.dark.main} !important`, // 🔥 DARK TEXT
        },

        /* === FOCUSED (keyboard) === */
        "& .MuiAutocomplete-option.Mui-focused": {
          backgroundColor: "rgba(255,255,255,0.14) !important",
          color: `${theme.palette.dark.main} !important`,
        },

        /* === SELECTED === */
        "& .MuiAutocomplete-option[aria-selected='true']": {
          backgroundColor: "rgba(255,255,255,0.18) !important",
          color: `${theme.palette.dark.main} !important`,
        },

        /* === DISABLED STATE === */
        "&.Mui-disabled .MuiAutocomplete-inputRoot": {
          backgroundColor: `${theme.palette.grey[600]} !important`,
          pointerEvents: "none !important",
        },
      }}
      /* ===============================
         CHIPS (SELECTED VALUES)
         ===============================
      */
      // renderTags={(value, getTagProps) =>
      //   value.map((option, index) => (
      //     <Chip
      //       {...getTagProps({ index })}
      //       key={option?._id ?? index}
      //       label={getOptionLabel(option)}
      //       size="small"
      //       sx={{
      //         height: "26px !important",
      //         display: "flex !important",
      //         alignItems: "center !important",
      //         margin: "4px 4px 4px 8px !important",
      //         padding: "0 8px !important",
      //         fontSize: "12px !important",

      //         backgroundColor: gradient
      //           ? `${theme.palette.info.main} !important`
      //           : "rgba(255,255,255,0.15) !important",

      //         border: gradient
      //           ? `1px solid ${theme.palette.grey[700]} !important`
      //           : "1px solid rgba(255,255,255,0.15) !important",

      //         color: `${theme.palette.dark.main} !important`,
      //         borderRadius: `${theme.borders.borderRadius.md} !important`,
      //       }}
      //     />
      //   ))
      // }
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const tagProps = getTagProps({ index });
          const isLastChip = value.length === 1;

          return (
            <Chip
              {...tagProps}
              key={option?._id ?? index}
              label={getOptionLabel(option)}
              size="small"
              onDelete={isLastChip ? undefined : tagProps.onDelete}
              sx={{
                height: "26px !important",
                display: "flex !important",
                alignItems: "center !important",
                margin: "4px 4px 4px 8px !important",
                maxWidth: "calc(100% - 44px)",
                minWidth: 0,
                padding: "0 8px !important",
                fontSize: "12px !important",
                backgroundColor: gradient
                  ? `${theme.palette.primary.main} !important`
                  : "rgba(255,255,255,0.15) !important",
                border: gradient
                  ? `1px solid ${theme.palette.grey[700]} !important`
                  : "1px solid rgba(255,255,255,0.15) !important",
                color: `${theme.palette.light.main} !important`,
                borderRadius: `${theme.borders.borderRadius.md} !important`,

                "& .MuiChip-deleteIcon": {
                  color: `${theme.palette.light.main} !important`,
                  opacity: 0.65,
                  fontSize: "16px !important",
                  marginLeft: "4px !important",
                  marginRight: "-2px !important",

                  "&:hover": {
                    color: `${theme.palette.light.main} !important`,
                    opacity: 1,
                  },
                },
              }}
            />
          );
        })
      }
      /* ===============================
         TEXTFIELD WRAPPER
         ===============================
      */
      renderInput={(params) => {
        if (multiple) {
          return (
            <TextField
              {...params}
              placeholder={value?.length ? "" : placeholder}
              variant="outlined"
              helperText={helperText}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "transparent !important",
                  padding: "0 !important",
                },
                "& .MuiInputBase-root": {
                  backgroundColor: "transparent !important",
                },
              }}
              InputProps={params.InputProps}
            />
          );
        }
        const hasValue = Boolean(value);

        return (
          <TextField
            {...params}
            placeholder={hasValue ? "" : placeholder}
            variant="outlined"
            helperText={helperText}
            InputProps={{
              ...params.InputProps,

              startAdornment: hasValue ? (
                <Chip
                  label={getOptionLabel(value)}
                  size="small"
                  sx={{
                    height: "26px",
                    marginLeft: "8px",
                    marginRight: "8px",
                    maxWidth: "calc(100% - 48px)",
                    minWidth: 0,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "12px",

                    color: theme.palette.dark.main,
                    borderRadius: theme.borders.borderRadius.md,
                    border: gradient ? "none" : "1px solid rgba(255,255,255,0.15)",
                  }}
                />
              ) : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent !important",
                padding: "0 !important",
              },
              "& .MuiInputBase-root": {
                backgroundColor: "transparent !important",
              },
            }}
          />
        );
      }}
      {...rest}
    />
  );
};

VuiAutocomplete.propTypes = {
  multiple: PropTypes.bool,
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  disabled: PropTypes.bool,
  gradient: PropTypes.bool,
  minWidth: PropTypes.number,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
};

export default VuiAutocomplete;
